/**
 * Network Logger Service
 * 
 * Tracks all API requests, responses, and SSE events for debugging.
 * Used by the Debug Panel to display network activity.
 */

export interface NetworkLogEntry {
  id: string;
  timestamp: number;
  type: 'request' | 'response' | 'sse' | 'error';
  method?: string;
  url?: string;
  status?: number;
  statusText?: string;
  requestBody?: any;
  responseBody?: any;
  headers?: Record<string, string>;
  error?: string;
  duration?: number;
  sseEvent?: {
    eventType: string;
    data: any;
  };
}

class NetworkLogger {
  private logs: NetworkLogEntry[] = [];
  private maxLogs = 500; // Keep last 500 entries
  private listeners: Array<(logs: NetworkLogEntry[]) => void> = [];

  constructor() {
    console.log('📡 Network Logger initialized');
  }

  /**
   * Log an API request
   */
  logRequest(config: {
    method: string;
    url: string;
    headers?: Record<string, string>;
    data?: any;
  }): string {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();

    const entry: NetworkLogEntry = {
      id,
      timestamp,
      type: 'request',
      method: config.method,
      url: config.url,
      requestBody: config.data,
      headers: config.headers,
    };

    this.addLog(entry);
    return id;
  }

  /**
   * Log an API response
   */
  logResponse(requestId: string, response: {
    status: number;
    statusText: string;
    data?: any;
    headers?: Record<string, string>;
    duration?: number;
  }): void {
    const entry: NetworkLogEntry = {
      id: `${requestId}-response`,
      timestamp: Date.now(),
      type: 'response',
      status: response.status,
      statusText: response.statusText,
      responseBody: response.data,
      headers: response.headers,
      duration: response.duration,
    };

    this.addLog(entry);
  }

  /**
   * Log an API error
   */
  logError(requestId: string, error: {
    message: string;
    status?: number;
    statusText?: string;
    data?: any;
    duration?: number;
  }): void {
    const entry: NetworkLogEntry = {
      id: `${requestId}-error`,
      timestamp: Date.now(),
      type: 'error',
      status: error.status,
      statusText: error.statusText,
      responseBody: error.data,
      error: error.message,
      duration: error.duration,
    };

    this.addLog(entry);
  }

  /**
   * Log an SSE event
   */
  logSSE(event: {
    eventType: string;
    data: any;
  }): void {
    const id = `sse-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();

    const entry: NetworkLogEntry = {
      id,
      timestamp,
      type: 'sse',
      sseEvent: {
        eventType: event.eventType,
        data: event.data,
      },
    };

    this.addLog(entry);
  }

  /**
   * Add a log entry and notify listeners
   */
  private addLog(entry: NetworkLogEntry): void {
    this.logs.push(entry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Notify listeners
    this.listeners.forEach(listener => listener([...this.logs]));
  }

  /**
   * Get all logs
   */
  getLogs(): NetworkLogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs filtered by type
   */
  getLogsByType(type: NetworkLogEntry['type']): NetworkLogEntry[] {
    return this.logs.filter(log => log.type === type);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
    this.listeners.forEach(listener => listener([]));
  }

  /**
   * Subscribe to log updates
   */
  onLogsUpdate(callback: (logs: NetworkLogEntry[]) => void): () => void {
    this.listeners.push(callback);
    // Immediately call with current logs
    callback([...this.logs]);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }
}

/**
 * Wrapper for fetch() that logs requests/responses to networkLogger
 * Use this instead of fetch() directly to see requests in Debug Panel Network tab
 */
export async function loggedFetch(
  url: string,
  options?: RequestInit
): Promise<Response> {
  const method = options?.method || 'GET';
  const requestId = networkLogger.logRequest({
    method,
    url,
    headers: options?.headers as Record<string, string>,
    data: options?.body ? (typeof options.body === 'string' ? JSON.parse(options.body) : options.body) : undefined,
  });

  const startTime = Date.now();

  try {
    const response = await fetch(url, options);
    const duration = Date.now() - startTime;

    // Clone response so we can read it without consuming it
    const clonedResponse = response.clone();
    let responseData: any;
    
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        responseData = await clonedResponse.json();
      } else {
        responseData = await clonedResponse.text();
      }
    } catch {
      // Response might not be JSON or might be empty
      responseData = null;
    }

    networkLogger.logResponse(requestId, {
      status: response.status,
      statusText: response.statusText,
      data: responseData,
      headers: Object.fromEntries(response.headers.entries()),
      duration,
    });

    return response;
  } catch (error: any) {
    const duration = Date.now() - startTime;
    networkLogger.logError(requestId, {
      message: error.message || 'Network error',
      duration,
    });
    throw error;
  }
}

// Export singleton instance
export const networkLogger = new NetworkLogger();

